import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FilePlus2, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { CompactTable } from '../../../components/ui/CompactTable';
import { legacyApi, GRN, GRNDetail, GrnOverview } from '../../../services/legacyApi';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { FlowCoachPanel } from '../../../components/workflow/FlowCoachPanel';
import { WorkflowRightSidebar } from '../../../components/workflow/WorkflowRightSidebar';
import { WorkflowRightPanel } from '../../../components/workflow/WorkflowRightPanel';
import { useAuth } from '../../../context/AuthContext';
import { getGrnStatusMeta } from '../utils/statuses';

export const LegacyGRNDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [grn, setGrn] = useState<(GRN & { details: GRNDetail[] }) | null>(null);
    const [overview, setOverview] = useState<GrnOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({ q: '' });

    const loadGrn = async () => {
        if (!id) return;
        setLoading(true);
        setLoadError('');
        return Promise.all([
            legacyApi.purchasing.getGRNDetail(Number(id)),
            legacyApi.purchasing.getGRNOverview(Number(id)).catch(() => null),
        ])
            .then(([grnDetail, overviewData]) => {
                setGrn(grnDetail);
                setOverview(overviewData);
            })
            .catch((error: any) => {
                console.error(error);
                setGrn(null);
                setOverview(null);
                setLoadError(error?.message || 'Failed to load GRN details');
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        void loadGrn();
    }, [id]);

    const { confirm } = useConfirm();

    const handleDelete = async () => {
        if (!id) return;
        const ok = await confirm('Delete this GRN permanently?', { variant: 'destructive', confirmText: 'Delete', cancelText: 'Cancel' });
        if (!ok) return;
        try {
            await legacyApi.purchasing.deleteGRN(Number(id));
            toast.success('GRN deleted');
            navigate('/legacy/grn');
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to delete GRN');
        }
    };

    const handleCancel = async () => {
        if (!id) return;
        const ok = await confirm('Cancel this GRN and reverse its stock/AP accrual impact?', { variant: 'destructive', confirmText: 'Cancel GRN', cancelText: 'Keep GRN' });
        if (!ok) return;
        try {
            await legacyApi.purchasing.transitionGRN(Number(id), { action: 'cancel' });
            toast.success('GRN cancelled');
            await loadGrn();
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to cancel GRN');
        }
    };

    const handleCreateBill = async () => {
        if (!grn) return;
        try {
            const bill = await legacyApi.purchasing.createBill({
                grn_id: grn.grn_id,
                bill_date: new Date().toISOString().split('T')[0],
                reference_no: grn.delivery_note_no || undefined,
            });
            toast.success(`Supplier bill #${bill.bill_id} created.`);
            navigate(`/purchasing/bills/${bill.bill_id}`);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create supplier bill');
        }
    };

    const formatQty = (value?: number) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const isMixedUomLine = (item: GRNDetail) => {
        const poUnit = String(item.unit_name || '').trim().toUpperCase();
        const txUnit = String(item.transaction_unit_name || '').trim().toUpperCase();
        return Boolean(txUnit) && Boolean(poUnit) && txUnit !== poUnit;
    };

    const totalTransactionQty = useMemo(
        () => (grn?.details || []).reduce((sum, item) => sum + Number(item.transaction_qty ?? item.quantity_received ?? 0), 0),
        [grn?.details],
    );
    const totalPoQty = useMemo(
        () => (grn?.details || []).reduce((sum, item) => sum + Number(item.total_qty_received ?? 0), 0),
        [grn?.details],
    );
    const hasMixedUom = useMemo(() => (grn?.details || []).some(isMixedUomLine), [grn?.details]);

    const columns = [
        { header: "Material", accessor: (item: GRNDetail) => item.material_desc || item.description || '-', className: '' },
        {
            header: "PO UOM / Qty",
            accessor: (item: GRNDetail) => `${item.unit_name || '-'} | Ord ${formatQty(item.qty_ordered)} | Rec ${formatQty(item.total_qty_received)} | Bal ${formatQty(item.remaining_qty)}`,
            className: '',
        },
        {
            header: "Received In",
            accessor: (item: GRNDetail) => {
                const receiveUnit = item.transaction_unit_name || item.unit_name || '-';
                const transactionQty = item.transaction_qty ?? item.quantity_received ?? 0;
                if (!isMixedUomLine(item)) {
                    return `${formatQty(transactionQty)} ${receiveUnit}`;
                }
                const baseQty = item.base_qty ?? transactionQty;
                return `${formatQty(transactionQty)} ${receiveUnit} | Base ${formatQty(baseQty)}`;
            },
            className: 'text-right',
        },
        {
            header: "Rate / Value",
            accessor: (item: GRNDetail) => `${formatQty(item.unit_price)} | ${formatQty(item.sub_total)}`,
            className: 'text-right',
        },
    ];
    const filteredDetails = useMemo(() => {
        const details = grn?.details || [];
        const query = state.q.trim().toLowerCase();
        if (!query) return details;
        return details.filter((item) =>
            String(item.material_desc || item.description || '').toLowerCase().includes(query) ||
            String(item.unit_name || '').toLowerCase().includes(query)
        );
    }, [grn?.details, state.q]);

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
    if (loadError) return <div className="p-8 text-center text-red-500">Unable to load GRN: {loadError}</div>;
    if (!grn) return <div className="p-8 text-center text-red-500">GRN not found</div>;
    const grnLockedByBill = (Boolean(overview?.bill_exists) || Boolean(grn.linked_bill_id || grn.auto_bill_id)) && Number(grn.bill_status_id || 0) !== 4;
    const grnLockedByBillMessage = 'GRN is locked because a supplier bill exists. Cancel the linked supplier bill first, then edit the GRN.';
    const grnCancelled = Number(grn.status_id || overview?.status_id || 1) === 4;
    const canEditGrn = hasPermission('purchasing.grn.edit') && !grnCancelled && !grnLockedByBill;
    const canCreateSupplierBill = hasPermission('purchasing.bill.create');
    const canCancelGrn = hasPermission('purchasing.grn.cancel');
    const canDeleteGrn = hasPermission('purchasing.grn.delete');
    const grnStatus = getGrnStatusMeta(grn.status_id, grn.status_name);
    const createBillAllowed = Boolean(overview?.can_create_bill ?? grn.can_create_bill);
    const cancelAllowed = Boolean(overview?.can_cancel ?? grn.can_cancel);
    const deleteAllowed = Boolean(overview?.can_delete ?? grn.can_delete);

    return (
        <div className="space-y-6 w-full p-6 pb-12">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/legacy/grn')}>
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold">Goods Received Note #{grn.grn_id}</h1>
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${grnStatus.badgeClass}`}>
                            {grnStatus.label}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        disabled={!canEditGrn}
                        title={!hasPermission('purchasing.grn.edit')
                            ? 'No permission to edit GRN'
                            : grnCancelled
                                ? 'Cancelled GRN cannot be edited'
                                : grnLockedByBill
                                    ? grnLockedByBillMessage
                                    : 'Edit'}
                        onClick={() => navigate(`/legacy/grn/edit/${grn.grn_id}`)}
                    >
                        <Pencil className="h-4 w-4 mr-2" /> Edit
                    </Button>
                    <Button
                        variant="outline"
                        disabled={!canCancelGrn || !cancelAllowed}
                        title={!canCancelGrn ? 'No permission to cancel GRN' : (overview?.cancel_reason || grn.cancel_reason || 'Cancel GRN')}
                        onClick={handleCancel}
                    >
                        <X className="h-4 w-4 mr-2" /> Cancel GRN
                    </Button>
                    <Button
                        variant="destructive"
                        disabled={!canDeleteGrn || !deleteAllowed}
                        title={!canDeleteGrn ? 'No permission to delete GRN' : (overview?.delete_reason || grn.delete_reason || 'Delete')}
                        onClick={handleDelete}
                    >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </Button>
                </div>
            </div>

            <div className="space-y-6">
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader><CardTitle>GRN Header</CardTitle></CardHeader>
                            <CardContent className="space-y-2">
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">GRN No</span>
                                    <span className="font-medium">{grn.grn_number || `HC/GRN/${String(grn.grn_id).padStart(4, '0')}`}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">PO No</span>
                                    <span className="font-medium">
                                        {String(grn.source_path || '').toUpperCase() === 'DIRECT_GRN'
                                            ? 'Direct (No PO)'
                                            : (grn.po_number || `HC/PO/${String(grn.po_id).padStart(4, '0')}`)}
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Status</span>
                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${grnStatus.badgeClass}`}>
                                        {grnStatus.label}
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Date Received</span>
                                    <span className="font-medium">{grn.date_received ? new Date(grn.date_received).toLocaleDateString() : '-'}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Received By</span>
                                    <span className="font-medium">{grn.received_by || '-'}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Delivery Note</span>
                                    <span className="font-medium">{grn.delivery_note_no || '-'}</span>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>Supplier & Remarks</CardTitle></CardHeader>
                            <CardContent className="space-y-2">
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Supplier</span>
                                    <span className="font-medium">{grn.supplier_name || '-'}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">{hasMixedUom ? 'Total Received (Txn Qty)' : 'Total Received'}</span>
                                    <span className="font-medium">{formatQty(hasMixedUom ? totalTransactionQty : (grn.total_received_qty ?? 0))}</span>
                                </div>
                                {hasMixedUom && (
                                    <div className="flex justify-between border-b pb-2">
                                        <span className="text-muted-foreground">Total Received (PO Qty)</span>
                                        <span className="font-medium">{formatQty(totalPoQty)}</span>
                                    </div>
                                )}
                                {hasMixedUom && (
                                    <div className="mt-4 rounded border bg-muted/20 p-3 text-xs text-muted-foreground">
                                        Mixed UOM receipt: PO quantities are shown in PO UOM, while the receipt quantity is shown in the GRN receive UOM.
                                    </div>
                                )}
                                {canCreateSupplierBill && (
                                    <div className="mt-4">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            disabled={!createBillAllowed}
                                            title={overview?.create_bill_reason || grn.create_bill_reason || 'Create Supplier Bill'}
                                            onClick={handleCreateBill}
                                        >
                                            <FilePlus2 className="h-4 w-4 mr-2" />
                                            Create Supplier Bill
                                        </Button>
                                    </div>
                                )}
                                {grn.remarks && (
                                    <div className="mt-4 p-3 bg-yellow-50 text-yellow-800 text-sm rounded">
                                        Remarks: {grn.remarks}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    <Card>
                        <CardHeader><CardTitle>Items</CardTitle></CardHeader>
                        <CardContent>
                            <TableFilterBar
                                searchValue={state.q}
                                onSearchChange={(value) => setFilter('q', value)}
                                searchPlaceholder="Search material lines..."
                                isDirty={isDirty}
                                onClear={clearAll}
                                rightSlot={`${filteredDetails.length} results`}
                            />
                            <div className="mt-3">
                                <CompactTable data={filteredDetails} columns={columns} keyField="grn_detail_id" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <WorkflowRightSidebar mode="overlay" title="GRN Workflow Context">
                    <WorkflowRightPanel title="Workflow Context" description="Related Purchase Order Flow">
                        {overview && (
                            <div className="space-y-3 rounded-md border bg-muted/20 p-3 text-xs">
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Path</span>
                                    <span className="font-medium">{overview.source_path === 'DIRECT_GRN' ? 'Direct GRN' : 'PO GRN'}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Next Action</span>
                                    <span className="font-medium">{overview.next_action || 'review'}</span>
                                </div>
                                <div className="rounded border bg-background p-2">
                                    <div className="font-medium">Billing</div>
                                    <div className="text-muted-foreground">{overview.create_bill_reason}</div>
                                    {overview.bill_exists && overview.bill_id && (
                                        <Button
                                            className="mt-2 h-7 px-2 text-xs"
                                            variant="outline"
                                            onClick={() => navigate(`/purchasing/bills/${overview.bill_id}`)}
                                        >
                                            Open Bill #{overview.bill_id}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                        {!!grn.po_id && <FlowCoachPanel entity="PO" entityId={grn.po_id} />}
                        <div className="space-y-4 pt-4 border-t">
                            <h4 className="font-medium text-sm">Flow Impact</h4>
                            <div className="text-sm text-slate-500 space-y-2">
                                <p>GRN receipt updates inventory quantity and weighted average cost for received materials.</p>
                                <p>Linked purchase order receiving progress is recalculated from GRN quantities.</p>
                                <p>Next handoff: reconcile supplier bill against received GRN lines.</p>
                            </div>
                        </div>
                    </WorkflowRightPanel>
                </WorkflowRightSidebar>
            </div>
        </div>
    );
};
